import { useEffect, useRef } from "react";
import { Eye, Clock, Heart } from "lucide-react";

const BENEFITS = [
  {
    icon: Eye,
    title: "Clarity",
    desc: "See the pattern behind your choices, your relationships and your doubts — so confusion turns into a clear, honest picture of who you are.",
  },
  {
    icon: Clock,
    title: "Timing",
    desc: "Know which seasons favour building and which favour rest, so you stop fighting the current and start moving when the sky actually supports it.",
  },
  {
    icon: Heart,
    title: "Self-Understanding",
    desc: "Understand the 'why' behind your instincts, your love language and your blind spots — with real compassion instead of judgement.",
  },
];

const STARS = [
  { x: 150, y: 70 },
  { x: 450, y: 30 },
  { x: 750, y: 70 },
];
const EXTRA_STARS = [
  { x: 60, y: 30 },
  { x: 300, y: 95 },
  { x: 600, y: 95 },
  { x: 840, y: 30 },
];
const LINE_LENGTH = Math.hypot(450 - 150, 30 - 70);

export const Benefits = () => {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const lineRefs = useRef([]);

  useEffect(() => {
    if (!window.gsap || !window.ScrollTrigger || !sectionRef.current) return;
    const gsapLib = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsapLib.registerPlugin(ScrollTrigger);

    gsapLib.set(cardRefs.current, { opacity: 0, y: 30 });
    gsapLib.set(lineRefs.current, { strokeDasharray: LINE_LENGTH, strokeDashoffset: LINE_LENGTH });

    const tl = gsapLib.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top 75%",
        end: "bottom 55%",
        scrub: true,
      },
    });

    tl.to(cardRefs.current[0], { opacity: 1, y: 0, duration: 0.08 }, 0)
      .to(lineRefs.current[0], { strokeDashoffset: 0, duration: 0.42, ease: "none" }, 0.08)
      .to(cardRefs.current[1], { opacity: 1, y: 0, duration: 0.08 }, 0.5)
      .to(lineRefs.current[1], { strokeDashoffset: 0, duration: 0.42, ease: "none" }, 0.58)
      .to(cardRefs.current[2], { opacity: 1, y: 0, duration: 0.08 }, 1.0);

    return () => tl.scrollTrigger && tl.scrollTrigger.kill();
  }, []);

  return (
    <section id="benefits" ref={sectionRef} className="cosmic-section max-w-7xl mx-auto">
      <div className="text-center mb-10">
        <p className="reveal cosmic-eyebrow text-sm mb-5">What You'll Walk Away With</p>
        <h2 className="reveal font-display text-3xl sm:text-4xl lg:text-5xl">A Reading That Actually Changes Something</h2>
      </div>

      <div className="relative">
        <svg
          data-testid="benefits-constellation"
          viewBox="0 0 900 130"
          preserveAspectRatio="none"
          className="absolute left-0 right-0 -top-6 w-full h-28 pointer-events-none"
        >
          {EXTRA_STARS.map((s, i) => (
            <circle key={i} cx={s.x} cy={s.y} r="1.6" fill="#f2eefb" opacity="0.3" />
          ))}
          <line
            ref={(el) => (lineRefs.current[0] = el)}
            data-testid="constellation-line-0"
            x1={STARS[0].x}
            y1={STARS[0].y}
            x2={STARS[1].x}
            y2={STARS[1].y}
            stroke="#E8C27A"
            strokeWidth="1.2"
            opacity="0.6"
          />
          <line
            ref={(el) => (lineRefs.current[1] = el)}
            data-testid="constellation-line-1"
            x1={STARS[1].x}
            y1={STARS[1].y}
            x2={STARS[2].x}
            y2={STARS[2].y}
            stroke="#E8C27A"
            strokeWidth="1.2"
            opacity="0.6"
          />
          {STARS.map((s, i) => (
            <circle key={i} cx={s.x} cy={s.y} r="3" fill="#E8C27A" />
          ))}
        </svg>

        <div className="grid md:grid-cols-3 gap-8 mt-16 relative z-10">
          {BENEFITS.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              ref={(el) => (cardRefs.current[i] = el)}
              data-testid={`benefit-card-${title.toLowerCase()}`}
              className="glass-card p-9 text-center"
            >
              <div className="w-14 h-14 rounded-full border border-[#E8C27A]/40 flex items-center justify-center mx-auto mb-6">
                <Icon size={24} className="text-[#E8C27A]" />
              </div>
              <h3 className="font-display text-xl mb-3 gold-text">{title}</h3>
              <p className="text-[#d8d3f0] font-light leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
